import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Starting Over at 60: What to Expect' },
  { id: 'before-you-start', label: 'Before You Start: The Honest Assessment' },
  { id: 'week1-2', label: 'Weeks 1-2: Building the Foundation' },
  { id: 'week3-4', label: 'Weeks 3-4: Adding Structure' },
  { id: 'month2', label: 'Month 2: Building Real Momentum' },
  { id: 'month3', label: 'Month 3: The Turning Point' },
  { id: 'beyond', label: 'Beyond Month 3: Long-Term Success' },
  { id: 'nutrition', label: 'Nutrition: The Other Half of the Equation' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'too-old-to-get-in-shape', title: 'Am I Too Old to Get in Shape? (The Answer Will Surprise You)', category: 'Motivation' },
  { slug: 'workout-motivation-for-older-men', title: 'Workout Motivation for Older Men: How to Stay Consistent', category: 'Motivation' },
  { slug: 'strength-training-for-men-over-60-beginners-guide', title: "The Complete Beginner's Guide to Strength Training for Men Over 60", category: 'Strength Training' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Getting Back in Shape at 60: Your Week-by-Week Plan',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-02-01',
    dateModified: '2024-04-06',
    description: 'A realistic week-by-week plan for getting back in shape at 60. No fluff, no extreme programs — just a proven roadmap that works for men over 60.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/getting-back-in-shape-at-60' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I start getting back in shape at 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'Start with 2 days per week of light resistance training and daily walking. Focus on building the habit before increasing intensity. Get medical clearance if you have any existing health conditions. The first 4 weeks are about establishing consistency, not pushing hard.' },
      },
      {
        '@type': 'Question',
        name: 'How long does it take to get back in shape at 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'Most men feel noticeably better within 3-4 weeks. Visible changes in body composition typically appear within 8-12 weeks. A significant transformation takes 6-12 months. The key is not stopping when progress feels slow.' },
      },
      {
        '@type': 'Question',
        name: 'Is it safe to start exercising at 60 after years of inactivity?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes, with appropriate precautions. Get medical clearance first, especially if you have cardiovascular conditions, diabetes, or joint issues. Start conservatively — lighter weights, shorter sessions, more rest. The risk of not exercising far outweighs the risk of starting carefully.' },
      },
      {
        '@type': 'Question',
        name: 'What should I eat when getting back in shape at 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'Prioritize protein — aim for 0.7-1g per pound of bodyweight. Don\'t drastically cut calories; focus on food quality instead. Reduce processed foods, increase vegetables and lean protein. Adequate protein is the single most important nutritional factor for body composition after 60.' },
      },
    ],
  },
]);

export default function GettingBackInShapeAt60Page() {
  return (
    <BlogLayout
      title="Getting Back in Shape at 60: Your Week-by-Week Plan"
      description="A realistic week-by-week plan for getting back in shape at 60. No extreme programs — just a proven roadmap that works for men over 60."
      keywords="getting back in shape at 60, back in shape after 60, fitness plan for men over 60, starting over at 60"
      canonicalPath="/blog/getting-back-in-shape-at-60"
      datePublished="2024-02-01"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Getting Back in Shape at 60: Your Week-by-Week Plan" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Motivation &amp; Mindset</span>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        Getting Back in Shape at 60: Your Week-by-Week Plan
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; February 1, 2024 &nbsp;·&nbsp; 11 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Starting Over at 60: What to Expect</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Getting back in shape at 60 after years of inactivity is one of the most common situations I hear about. Maybe you were active in your 30s and 40s, then life got in the way. Maybe you've never really had a consistent fitness routine. Either way, you're here now, and that's what matters.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The good news: your body has a remarkable ability to respond to exercise, even after years of inactivity. The <a href="https://www.nia.nih.gov/health/exercise-physical-activity" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">National Institute on Aging</a> confirms that previously sedentary older adults can achieve significant improvements in strength, cardiovascular fitness, and body composition within just a few months of starting a structured program.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The bad news: most men try to do too much too soon and end up injured or burned out within 6 weeks. This plan is designed to prevent that.
        </p>
      </section>

      <section id="before-you-start" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Before You Start: The Honest Assessment</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Before you do a single workout, do these three things:
        </p>
        <ol className="space-y-4 mb-4">
          {[
            { title: 'Get medical clearance', desc: 'If you have cardiovascular disease, diabetes, hypertension, or joint issues, talk to your doctor first. Most will enthusiastically support you — but you need to know your starting point and any limitations.' },
            { title: 'Take baseline measurements', desc: 'Weigh yourself. Measure your waist. Note your resting heart rate. Write down how many pushups and squats you can do. These numbers will become your proof of progress.' },
            { title: 'Set a realistic 90-day goal', desc: 'Not "get ripped." Something specific and achievable: lose 10 lbs, complete 3 workouts per week for 12 weeks, walk 30 minutes daily. Specific goals create specific actions.' },
          ].map((item, i) => (
            <li key={item.title} className="flex items-start gap-4 bg-[#111111] border border-white/10 rounded-lg p-5">
              <span className="w-7 h-7 flex-shrink-0 bg-[#c8a96e] text-black text-xs font-black rounded-full flex items-center justify-center">{i + 1}</span>
              <div>
                <strong className="text-white block mb-1">{item.title}</strong>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section id="week1-2" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Weeks 1-2: Building the Foundation</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The goal of weeks 1-2 is simple: establish the habit without getting hurt. That's it. Don't try to do more.
        </p>
        <div className="bg-[#111111] border border-white/10 rounded-lg p-5 mb-4">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-3">Weeks 1-2 Schedule</p>
          <ul className="space-y-2">
            {[
              { day: 'Monday', activity: '20-min walk + 10-min bodyweight circuit (squats, wall pushups, seated rows)' },
              { day: 'Tuesday', activity: 'Rest or gentle walking' },
              { day: 'Wednesday', activity: '20-min walk + 10-min bodyweight circuit' },
              { day: 'Thursday', activity: 'Rest' },
              { day: 'Friday', activity: '20-min walk + 10-min bodyweight circuit' },
              { day: 'Weekend', activity: 'Light walking, rest, recovery' },
            ].map((item) => (
              <li key={item.day} className="flex items-start gap-3 text-sm">
                <span className="text-[#c8a96e] font-bold w-24 flex-shrink-0">{item.day}</span>
                <span className="text-gray-300">{item.activity}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-gray-300 leading-relaxed">
          This feels easy. That's intentional. You're building the habit, not testing your limits. Expect some soreness — that's normal. Expect to feel better by the end of week 2.
        </p>
      </section>

      <section id="week3-4" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Weeks 3-4: Adding Structure</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          By week 3, the habit is forming. Now we add a little more structure and intensity — but still nothing extreme.
        </p>
        <div className="bg-[#111111] border border-white/10 rounded-lg p-5 mb-4">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-3">Weeks 3-4 Schedule</p>
          <ul className="space-y-2">
            {[
              { day: 'Monday', activity: '30-min walk + 20-min dumbbell workout (goblet squats, dumbbell rows, chest press)' },
              { day: 'Wednesday', activity: '30-min walk + 20-min dumbbell workout' },
              { day: 'Friday', activity: '30-min walk + 20-min dumbbell workout' },
              { day: 'Other days', activity: 'Light walking, stretching, rest' },
            ].map((item) => (
              <li key={item.day} className="flex items-start gap-3 text-sm">
                <span className="text-[#c8a96e] font-bold w-24 flex-shrink-0">{item.day}</span>
                <span className="text-gray-300">{item.activity}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-gray-300 leading-relaxed">
          Start with light dumbbells — lighter than you think you need. Perfect form matters more than weight at this stage. You'll add weight in month 2.
        </p>
      </section>

      <section id="month2" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Month 2: Building Real Momentum</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Month 2 is where things start to get exciting. You've built the habit. Your body is adapting. Now you progressively increase the challenge.
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Increase dumbbell weight by 5 lbs on exercises where you can complete all reps with good form',
            'Extend workout sessions to 30-35 minutes',
            'Add one additional exercise per session (lunges, shoulder press, or Romanian deadlifts)',
            'Increase walking to 35-40 minutes on training days',
            'Start tracking your workouts in a notebook or app',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-arrow-right-s-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed">
          By the end of month 2, most men report significantly better energy, improved sleep, and clothes fitting differently. The scale may or may not have moved much — but your body composition is changing.
        </p>
      </section>

      <section id="month3" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Month 3: The Turning Point</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Month 3 is when most men either quit or commit. The initial excitement has worn off. Progress may feel slower. This is the critical period.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Here's what's actually happening in month 3: your body is making deeper adaptations — hormonal changes, cardiovascular improvements, bone density increases. These don't show up on the scale, but they're happening.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Push through month 3 and you'll reach a point where training feels natural, almost automatic. That's the turning point. That's when getting back in shape at 60 stops being a struggle and starts being a lifestyle.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The <a href="https://www.health.harvard.edu/staying-healthy/strength-training-builds-more-than-muscles" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">Harvard Health research</a> on exercise adherence shows that men who make it past the 12-week mark are dramatically more likely to maintain their fitness habits long-term. Month 3 is the finish line of the hardest part.
        </p>
      </section>

      <section id="beyond" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Beyond Month 3: Long-Term Success</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          After 3 months, you're no longer "getting back in shape" — you're just a man who trains. The focus shifts from building the habit to optimizing the program.
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Continue progressive overload — always working toward slightly more than last time',
            'Consider adding a 4th training day if recovery allows',
            'Reassess your nutrition — protein intake becomes increasingly important',
            'Schedule a check-in with your doctor to review health markers',
            'Set new 90-day goals based on where you are now',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-checkbox-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed">
          If you want a complete, done-for-you program that takes you through all of this step by step, the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> is exactly that — a structured system built for men getting back in shape after 60.
        </p>
      </section>

      <section id="nutrition" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Nutrition: The Other Half of the Equation</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          You can't out-train a bad diet. But you also don't need to overhaul everything at once. Start with these fundamentals:
        </p>
        <div className="space-y-3 mb-4">
          {[
            { title: 'Protein first', desc: 'Aim for 0.7-1g per pound of bodyweight. This is the single most important nutritional change you can make. Most men over 60 eat far too little protein.' },
            { title: 'Don\'t crash diet', desc: 'Severe calorie restriction causes muscle loss — the opposite of what you want. A modest deficit of 300-500 calories per day is plenty.' },
            { title: 'Reduce processed food', desc: 'You don\'t need to be perfect. Just reduce the obvious junk — fast food, sugary drinks, processed snacks. Small changes compound.' },
            { title: 'Stay hydrated', desc: 'Dehydration impairs performance and recovery. Aim for 8-10 glasses of water per day, more on training days.' },
          ].map((item) => (
            <div key={item.title} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <p className="text-white text-sm font-bold mb-1">{item.title}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'How do I start getting back in shape at 60?', a: 'Start with 2 days per week of light resistance training and daily walking. Focus on building the habit before increasing intensity. Get medical clearance if you have any existing health conditions.' },
            { q: 'How long does it take to get back in shape at 60?', a: 'Most men feel noticeably better within 3-4 weeks. Visible changes in body composition typically appear within 8-12 weeks. A significant transformation takes 6-12 months.' },
            { q: 'Is it safe to start exercising at 60 after years of inactivity?', a: 'Yes, with appropriate precautions. Get medical clearance first, especially if you have cardiovascular conditions, diabetes, or joint issues. Start conservatively — lighter weights, shorter sessions, more rest.' },
            { q: 'What should I eat when getting back in shape at 60?', a: 'Prioritize protein — aim for 0.7-1g per pound of bodyweight. Don\'t drastically cut calories; focus on food quality instead. Adequate protein is the single most important nutritional factor for body composition after 60.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">Skip the Guesswork. Follow the Blueprint.</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint gives you the exact week-by-week plan to get back in shape — joint-safe, progressive, and built specifically for men over 60.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
